import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { EVENT_TYPES, PULSE_METHOD } from "@/lib/pulseConstants";
import Marquee from "@/components/shared/Marquee";

export default function ServicesPage() {
    useEffect(() => {
        document.title = "Services — REFORM Pulse";
    }, []);

    return (
        <div>
            {/* Hero */}
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-16 overflow-hidden" data-testid="services-page">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=2000&q=80')` }} />
                    <div className="absolute inset-0 hero-radial" />
                    <div className="absolute inset-0 bg-[#07070A]/60" />
                </div>
                <div className="container-editorial py-16">
                    <SectionEyebrow index="//" label="Services" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            Seven ways we help brands go <span className="text-[#FF2A2A]">live</span>.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            Every format is designed to earn attention, produce content and connect back
                            to the campaign, brand or business objective driving it.
                        </p>
                    </Reveal>
                </div>
            </section>

            <Marquee />

            {/* Services grid */}
            <section className="section-pad">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
                        {EVENT_TYPES.map((s, i) => (
                            <Reveal key={s.key} delay={0.03 * i} className={i === 0 ? "lg:col-span-12" : "lg:col-span-6"}>
                                <ServiceCard service={s} index={i + 1} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Method */}
            <section className="section-pad relative border-y border-white/10 bg-[#0A0B10]">
                <div className="container-editorial">
                    <SectionEyebrow index="//" label="How we work" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance mb-12">
                            The Pulse <span className="text-[#FF2A2A]">Method</span>.
                        </h2>
                    </Reveal>

                    <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
                        {PULSE_METHOD.map((m, i) => (
                            <AccordionItem key={m.step} value={`item-${i}`} className="border-white/10">
                                <AccordionTrigger className="py-6 hover:no-underline text-left">
                                    <div className="flex items-center gap-6">
                                        <span className="font-display text-4xl text-white/60 group-hover:text-[#FF2A2A]">
                                            {m.step}
                                        </span>
                                        <span className="font-display text-2xl md:text-3xl text-white uppercase">
                                            {m.title}
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="pl-6 md:pl-24 pb-8 text-white/70 text-base leading-relaxed">
                                    {m.body}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA */}
            <section className="section-pad text-center">
                <div className="container-editorial">
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Ready to <span className="text-[#FF2A2A]">brief us</span>?
                        </h2>
                    </Reveal>
                    <div className="mt-8">
                        <Button asChild className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-14 px-8 text-base">
                            <Link to="/contact" data-testid="services-cta-brief" className="inline-flex items-center gap-2">
                                Start Your Pulse Brief <ArrowUpRight size={18} />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ServiceCard({ service, index }) {
    return (
        <motion.div
            whileHover={{ y: -3 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="card-sheen h-full rounded-2xl border border-white/10 bg-[#0B0C10] p-6 md:p-8 relative overflow-hidden"
            data-testid={`service-card-${service.key}`}
        >
            <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#FF2A2A]">
                    Service / 0{index}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">{service.tag}</span>
            </div>
            <h3 className="font-display text-4xl md:text-5xl text-white mb-4 leading-none">{service.title}</h3>
            <p className="body-md mb-6 max-w-2xl">{service.hook}</p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {service.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/85">
                        <Check size={14} className="text-[#FF2A2A] mt-0.5" />
                        {b}
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}
